import { useSyncExternalStore } from "react";
import { EXCHANGE_TIME_ZONE, formatTime, resolveTimeZone, useSettings } from "@helm/core";
import { riskActionToast, type RiskToast } from "@helm/ui";
import { formatInstantWithZone } from "../time/useDisplayZone";
import { Icon } from "./Icon";

/**
 * Toasts (SPEC §17.8: a toast instead of a desktop notification while HELM is focused). Announced
 * through a polite live region; risk toasts are assertive. Auto-dismiss after 6 s, or on × (persistent ones, e.g. gateway
 * errors, only on ×).
 */

export type ToastAction = { label: string; run: () => void };
export type Toast = RiskToast & { id: number; assertive: boolean; persist?: boolean; action?: ToastAction };

/** Toasts on screen at once; the oldest expiring one goes first, persistent ones last. */
const MAX_TOASTS = 4;

let toasts: readonly Toast[] = [];
let seq = 0;
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

/** `persist`: stays until dismissed (critical gateway errors); `ttlMs` is then ignored. */
export function pushToast(
  t: RiskToast,
  opts: { assertive?: boolean; ttlMs?: number; persist?: boolean; action?: ToastAction } = {},
): number {
  const id = ++seq;
  const next: Toast[] = [
    ...toasts,
    {
      ...t,
      id,
      assertive: opts.assertive ?? false,
      ...(opts.persist ? { persist: true } : {}),
      ...(opts.action ? { action: opts.action } : {}),
    },
  ];
  while (next.length > MAX_TOASTS) {
    const i = next.findIndex((x) => x.persist !== true);
    next.splice(i >= 0 ? i : 0, 1);
  }
  toasts = next;
  emit();
  if (!opts.persist) {
    setTimeout(() => {
      dismissToast(id);
    }, opts.ttlMs ?? 6000);
  }
  return id;
}

export function dismissToast(id: number): void {
  const next = toasts.filter((t) => t.id !== id);
  if (next.length === toasts.length) return;
  toasts = next;
  emit();
}

/**
 * Show a gateway `RiskAction` (domain serde JSON, e.g. `{"type":"liquidate","limit":"pdpt_soft"}`).
 * The trading-stream handler calls this once risk events are wired (Phase 2); unknown actions are
 * ignored.
 */
export function showRiskAction(action: unknown): void {
  const zone = resolveTimeZone(useSettings.getState().settings.display_time_zone);
  const t = riskActionToast(action, (ms) => {
    const ct =
      EXCHANGE_TIME_ZONE === zone ? "" : ` (${formatTime(ms, EXCHANGE_TIME_ZONE, { seconds: false })} CT)`;
    return `${formatInstantWithZone(ms, zone)}${ct}`;
  });
  if (t) pushToast(t, { assertive: t.tone === "danger", ttlMs: t.tone === "danger" ? 12_000 : 8000 });
}

/** Same notice (code + symbol) again within this window: not re-toasted. */
const MARKET_NOTICE_DEDUPE_MS = 30_000;
const lastMarketNotice = new Map<string, number>();

/**
 * Toast a gateway market-data notice (`MD_GAP`, `MD_SYMBOL_LIMIT`, …). `text` is already the
 * concise UI message (`noticeMessage`); repeats per code + symbol are suppressed for 30 s.
 */
export function showMarketNotice(code: string, text: string, key: string): void {
  const k = `${code}|${key}`;
  const now = Date.now();
  const last = lastMarketNotice.get(k);
  if (last !== undefined && now - last < MARKET_NOTICE_DEDUPE_MS) return;
  lastMarketNotice.set(k, now);
  if (lastMarketNotice.size > 256) lastMarketNotice.clear();
  pushToast({ tone: "warn", title: "Market data", body: text }, { ttlMs: 8000 });
}

const subscribe = (l: () => void): (() => void) => {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
};
const snapshot = (): readonly Toast[] => toasts;

export function ToastHost(): React.JSX.Element {
  const list = useSyncExternalStore(subscribe, snapshot);
  const polite = list.filter((t) => !t.assertive);
  const loud = list.filter((t) => t.assertive);
  const render = (t: Toast): React.JSX.Element => (
    <div key={t.id} className={`toast toast-${t.tone}`}>
      <div className="toast-text">
        <div className="toast-title">{t.title}</div>
        <div className="toast-body">{t.body}</div>
      </div>
      {t.action && (
        <button
          type="button"
          className="btn btn-sm toast-action"
          onClick={() => {
            t.action?.run();
            dismissToast(t.id);
          }}
        >
          {t.action.label}
        </button>
      )}
      <button
        type="button"
        className="icon-btn"
        aria-label="Dismiss notification"
        onClick={() => {
          dismissToast(t.id);
        }}
      >
        <Icon name="close" size={12} />
      </button>
    </div>
  );
  return (
    <div className="toasts">
      <div role="status" aria-live="polite" className="toast-stack">
        {polite.map(render)}
      </div>
      <div role="alert" aria-live="assertive" className="toast-stack">
        {loud.map(render)}
      </div>
    </div>
  );
}
